import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import usePdfStatus from '../../hooks/usePdfStatus';
import { 
  Box, 
  Typography, 
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  Snackbar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import aiAnswerIcon from '../../assets/icons/ai.svg';
import userIcon from '../../assets/icons/user.svg';
import headerIcon from '../../assets/icons/header.svg';
import { 
  checkApiHealth, 
  fetchBotResponse, 
  createConversationRequest,
  clearRequestCache 
} from '../../services/conversationalService';
import { getSessionUUID } from '../../utils/uuid';

const LW_Agent_Conversational = ({ product, hideHeader = false }) => {
  const [question, setQuestion] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memoryUuid, setMemoryUuid] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [showCopySnackbar, setShowCopySnackbar] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const hasInitialized = useRef(false);
  const sessionId = useRef(getSessionUUID()); // Unique session ID for this browser tab
  const [apiHealthStatus, setApiHealthStatus] = useState({ isHealthy: null, lastChecked: null, checking: false });
  const pdfStatus = usePdfStatus(product);

  // Session-based storage keys
  const getMemoryUuidKey = useCallback(() => {
    const productId = product?.id || product?.unique_id || product?.product_id;
    return `memoryUuid_${sessionId.current}_${productId}`;
  }, [product]);

  const getConversationKey = useCallback(() => {
    const productId = product?.id || product?.unique_id || product?.product_id;
    return `conversations_${sessionId.current}_${productId}`;
  }, [product]);

  // Save memoryUuid to sessionStorage for persistence across component remounts
  const saveMemoryUuid = useCallback((uuid) => {
    try {
      if (uuid) {
        sessionStorage.setItem(getMemoryUuidKey(), uuid);
        console.log('Memory UUID saved to session:', uuid);
      } else {
        sessionStorage.removeItem(getMemoryUuidKey());
        console.log('Memory UUID cleared from session');
      }
    } catch (error) {
      console.warn('Failed to save memory UUID to session storage:', error);
    }
  }, [getMemoryUuidKey]);

  // Load memoryUuid from sessionStorage
  const loadMemoryUuid = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(getMemoryUuidKey());
      if (stored) {
        console.log('Memory UUID loaded from session:', stored);
        return stored;
      }
    } catch (error) {
      console.warn('Failed to load memory UUID from session storage:', error);
    }
    return null;
  }, [getMemoryUuidKey]);

  // Save conversations to sessionStorage
  const saveConversations = useCallback((convs) => {
    try {
      if (convs && convs.length > 0) {
        sessionStorage.setItem(getConversationKey(), JSON.stringify(convs));
      } else {
        sessionStorage.removeItem(getConversationKey());
      }
    } catch (error) {
      console.warn('Failed to save conversations to session storage:', error);
    }
  }, [getConversationKey]);

  // Load conversations from sessionStorage
  const loadConversations = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(getConversationKey());
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log('Conversations loaded from session:', parsed.length, 'messages');
        return parsed;
      }
    } catch (error) {
      console.warn('Failed to load conversations from session storage:', error);
    }
    return [];
  }, [getConversationKey]);

  // Clear session data
  const clearSessionData = useCallback(() => {
    try {
      sessionStorage.removeItem(getMemoryUuidKey());
      sessionStorage.removeItem(getConversationKey());
      console.log('Session data cleared');
    } catch (error) {
      console.warn('Failed to clear session data:', error);
    }
  }, [getMemoryUuidKey, getConversationKey]);

  // Update memoryUuid with persistence
  const updateMemoryUuid = useCallback((uuid) => {
    setMemoryUuid(uuid);
    saveMemoryUuid(uuid);
  }, [saveMemoryUuid]);

  // Clean up old session data that may have expired
  const cleanupExpiredSessions = useCallback(() => {
    try {
      // Get all session storage keys
      const keys = Object.keys(sessionStorage);
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      keys.forEach(key => {
        if (key.startsWith('memoryUuid_') || key.startsWith('conversations_')) {
          try {
            const stored = sessionStorage.getItem(key);
            if (stored) {
              // For conversation data, check if it's too old
              if (key.startsWith('conversations_')) {
                const conversations = JSON.parse(stored);
                if (Array.isArray(conversations) && conversations.length > 0) {
                  const lastTimestamp = new Date(conversations[conversations.length - 1].timestamp);
                  if (now - lastTimestamp.getTime() > maxAge) {
                    sessionStorage.removeItem(key);
                    console.log('Cleaned up expired conversation data:', key);
                  }
                }
              }
              // For memory UUIDs, we'll let the API handle expiry
            }
          } catch (e) {
            // Remove corrupted data
            sessionStorage.removeItem(key);
            console.log('Removed corrupted session data:', key);
          }
        }
      });
    } catch (error) {
      console.warn('Error during session cleanup:', error);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  // Optimized scroll effects with requestAnimationFrame
  useEffect(() => {
    requestAnimationFrame(scrollToBottom);
  }, [conversations, scrollToBottom]);
  
  useEffect(() => { 
    if (loading) {
      const timeoutId = setTimeout(() => requestAnimationFrame(scrollToBottom), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [loading, scrollToBottom]);

  useEffect(() => {
    if (scrollToBottom) {
      requestAnimationFrame(scrollToBottom);
    }
  }, [scrollToBottom]);


  // Initialize memory UUID when component mounts - load from session if available
  useEffect(() => {
    if (product && !hasInitialized.current) {
      console.log('Component initialized - loading session data');
      hasInitialized.current = true;
      setIsInitializing(false);
      
      // Clean up any expired session data first
      cleanupExpiredSessions();
      
      // Load persisted session data
      const storedMemoryUuid = loadMemoryUuid();
      const storedConversations = loadConversations();
      
      if (storedMemoryUuid) {
        setMemoryUuid(storedMemoryUuid);
        console.log('Restored memory UUID from session:', storedMemoryUuid);
      }
      
      if (storedConversations && storedConversations.length > 0) {
        setConversations(storedConversations);
        setShowSuggestions(false); // Hide suggestions if we have conversation history
        console.log('Restored conversation history:', storedConversations.length, 'messages');
      }
      
      // Clear request cache for this product to ensure fresh requests
      const productId = product?.id || product?.unique_id || product?.product_id;
      clearRequestCache(productId);
    }
  }, [product, loadMemoryUuid, loadConversations, cleanupExpiredSessions]);

  // Save conversations to session storage whenever they change
  useEffect(() => {
    if (hasInitialized.current) {
      saveConversations(conversations);
    }
  }, [conversations, saveConversations]);

  // Cleanup effect - clear old session data when component unmounts
  useEffect(() => {
    return () => {
      // Optional: Clear session data on unmount (commented out to preserve across navigations)
      // clearSessionData();
    };
  }, [clearSessionData]);
  
  // Reduced API health check - only on mount and less frequent intervals
  useEffect(() => {
    let isMounted = true;
    
    const check = async () => { 
      if (isMounted) await performApiHealthCheck(); 
    };
    
    check(); // Check once on mount
    const healthCheckInterval = setInterval(check, 300000); // Check every 5 minutes instead of 1 minute
    
    return () => { 
      isMounted = false; 
      clearInterval(healthCheckInterval); 
    };
  }, []);

  const performApiHealthCheck = useCallback(async () => {
    if (apiHealthStatus.checking) return;
    setApiHealthStatus(prev => ({ ...prev, checking: true }));
    
    try {
      const healthResult = await checkApiHealth();
      setApiHealthStatus({ ...healthResult, checking: false });
    } catch (error) {
      setApiHealthStatus({ isHealthy: false, lastChecked: new Date(), checking: false });
    }
  }, [apiHealthStatus.checking]);

  // Memoized API status indicator component
  const ApiStatusIndicator = useMemo(() => {
    const { isHealthy, checking } = apiHealthStatus;
    const statusMap = {
      checking: { color: '#999', tooltip: 'Checking API status...' },
      healthy: { color: '#4caf50', tooltip: 'Q&A API is online and ready' },
      unhealthy: { color: '#f44336', tooltip: 'Q&A API is offline or starting up.' },
      initial: { color: '#999', tooltip: 'Checking API status...' }
    };
    
    const getStatus = () => {
      if (checking) return 'checking';
      if (isHealthy === true) return 'healthy';
      if (isHealthy === false) return 'unhealthy';
      return 'initial';
    };
    
    const currentStatus = getStatus();
    const { color, tooltip } = statusMap[currentStatus];
    
    return (
      <FiberManualRecordIcon 
        sx={{ fontSize: '0.6rem', color }}
        title={tooltip}
      />
    );
  }, [apiHealthStatus]);

  // Optimized streaming response with better performance
  const streamResponse = useCallback((botMessageId, responseMessage) => {
    let currentIndex = 0;
    let animationId;
    
    const typeNextCharacter = () => {
      if (currentIndex < responseMessage.length) {
        // Batch updates every 5 characters for better performance
        const nextIndex = Math.min(currentIndex + 5, responseMessage.length);
        const partialMessage = responseMessage.substring(0, nextIndex);
        
        setConversations(prev => prev.map(msg => 
          msg.id === botMessageId
            ? { ...msg, message: partialMessage, isStreaming: true }
            : msg
        ));
        
        currentIndex = nextIndex;
        
        // Scroll less frequently - every 25 characters
        if (currentIndex % 25 === 0) {
          requestAnimationFrame(scrollToBottom);
        }
        
        animationId = requestAnimationFrame(typeNextCharacter);
      } else {
        setConversations(prev => prev.map(msg => 
          msg.id === botMessageId
            ? { ...msg, message: responseMessage, isStreaming: false, success: true }
            : msg
        ));
        requestAnimationFrame(scrollToBottom);
      }
    };
    
    animationId = requestAnimationFrame(typeNextCharacter);
    
    // Return cleanup function
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [scrollToBottom]);

  const suggestionQuestions = useMemo(() => [
    'How has this been rated for outdoor use?',
    'What is the minimum ceiling height required for safe installation?', 
    'What are the dimensions of this fan?'
  ], []);

  const handleQuestionSubmit = useCallback(async () => {
    if (!question.trim() || loading) return;
    
    // Hide suggestions after first question
    setShowSuggestions(false);
    
    const userQuestion = question.trim();
    const userMessageId = Date.now();
    const botMessageId = userMessageId + 10;
    
    // Batch state updates
    setConversations(prev => [
      ...prev, 
      { id: userMessageId, type: 'user', message: userQuestion, timestamp: new Date() },
      { id: botMessageId, type: 'bot', message: '', timestamp: new Date(), success: true, isStreaming: false }
    ]);
    setQuestion('');
    setLoading(true);
    setError(null);
    
    try {
      // Create request with or without PDF data based on memoryUuid existence
      const requestBody = createConversationRequest(
        product, 
        userQuestion, 
        memoryUuid, 
        memoryUuid ? { status: 'has_memory', loading: false } : pdfStatus
      );

      // Convert problematic PDF links if present
      if (requestBody.batch && requestBody.batch[0] && requestBody.batch[0].pdfLinks) {
        const originalPdfLinks = requestBody.batch[0].pdfLinks;
        const convertedPdfLinks = originalPdfLinks.map(link => {
          if (link.startsWith('//s1.img-b.com/')) {
            return link.replace('//s1.img-b.com/', 'https://storage.googleapis.com/buildcom-pim-prd/');
          }
          return link;
        });
        requestBody.batch[0].pdfLinks = convertedPdfLinks;
      }

      const data = await fetchBotResponse(requestBody);
      
      let responseMessage = 'The AI service responded but no content was received.';
      if (data.predictions && data.predictions[0]) {
        const prediction = data.predictions[0];
        
        // Extract memoryUuid from first response if we don't have one
        if (!memoryUuid && prediction.memoryUuid) {
          updateMemoryUuid(prediction.memoryUuid);
          console.log('New memory UUID acquired from API:', prediction.memoryUuid);
        }
        
        responseMessage = prediction.response || prediction.content || prediction.answer || prediction.text || responseMessage;
      }
      
      setLoading(false);
      streamResponse(botMessageId, responseMessage);
    } catch (error) {
      console.log('Error details:', error);
      
      // Check if it's a memory expired error
      const isMemoryExpired = error.message?.includes('memory-expired-error') || 
                             error.message?.includes('Memory UUID not found') ||
                             (error.message?.includes('400') && memoryUuid);
      
      if (isMemoryExpired && memoryUuid) {
        console.log('Memory UUID expired, retrying with fresh session...');
        updateMemoryUuid(null); // Clear expired UUID from state and session
        
        // Clear the request cache to ensure fresh requests
        const productId = product?.id || product?.unique_id || product?.product_id;
        clearRequestCache(productId);
        
        // Retry the request without memoryUuid to get a fresh session
        try {
          const retryRequestBody = createConversationRequest(
            product, 
            userQuestion, 
            null, // No memoryUuid for fresh session
            pdfStatus
          );

          // Convert problematic PDF links if present
          if (retryRequestBody.batch && retryRequestBody.batch[0] && retryRequestBody.batch[0].pdfLinks) {
            const originalPdfLinks = retryRequestBody.batch[0].pdfLinks;
            const convertedPdfLinks = originalPdfLinks.map(link => {
              if (link.startsWith('//s1.img-b.com/')) {
                return link.replace('//s1.img-b.com/', 'https://storage.googleapis.com/buildcom-pim-prd/');
              }
              return link;
            });
            retryRequestBody.batch[0].pdfLinks = convertedPdfLinks;
          }

          const retryData = await fetchBotResponse(retryRequestBody);
          
          let retryResponseMessage = 'The AI service responded but no content was received.';
          if (retryData.predictions && retryData.predictions[0]) {
            const prediction = retryData.predictions[0];
            
            // Extract new memoryUuid
            if (prediction.memoryUuid) {
              setMemoryUuid(prediction.memoryUuid);
            }
            
            retryResponseMessage = prediction.response || prediction.content || prediction.answer || prediction.text || retryResponseMessage;
          }
          
          setLoading(false);
          streamResponse(botMessageId, retryResponseMessage);
          return; // Success, exit the function
        } catch (retryError) {
          console.error('Retry failed:', retryError);
        }
      }
      
      // If we get here, either it wasn't a memory error or the retry failed
      const simpleErrorMsg = 'Sorry, something went wrong. Please try again.';
      setConversations(prev => prev.map(msg => msg.id === botMessageId ? { ...msg, message: simpleErrorMsg, isStreaming: false, success: false } : msg));
      setError(null); // Don't show the error alert
      setLoading(false);
    }
  }, [question, loading, memoryUuid, product, pdfStatus, streamResponse, updateMemoryUuid]);

  const handleSuggestionClick = useCallback(async (suggestionText) => {
    if (loading) return;
    
    setShowSuggestions(false);
    
    const userMessageId = Date.now();
    const botMessageId = userMessageId + 10;
    
    // Batch state updates
    setConversations(prev => [
      ...prev, 
      { id: userMessageId, type: 'user', message: suggestionText, timestamp: new Date() },
      { id: botMessageId, type: 'bot', message: '', timestamp: new Date(), success: true, isStreaming: false }
    ]);
    setLoading(true);
    setError(null);
    
    try {
      // Create request with or without PDF data based on memoryUuid existence
      const requestBody = createConversationRequest(
        product, 
        suggestionText, 
        memoryUuid, 
        memoryUuid ? { status: 'has_memory', loading: false } : pdfStatus
      );

      // Convert problematic PDF links if present
      if (requestBody.batch && requestBody.batch[0] && requestBody.batch[0].pdfLinks) {
        const originalPdfLinks = requestBody.batch[0].pdfLinks;
        const convertedPdfLinks = originalPdfLinks.map(link => {
          if (link.startsWith('//s1.img-b.com/')) {
            return link.replace('//s1.img-b.com/', 'https://storage.googleapis.com/buildcom-pim-prd/');
          }
          return link;
        });
        requestBody.batch[0].pdfLinks = convertedPdfLinks;
      }

      const data = await fetchBotResponse(requestBody);
      
      let responseMessage = 'The AI service responded but no content was received.';
      if (data.predictions && data.predictions[0]) {
        const prediction = data.predictions[0];
        
        // Extract memoryUuid from first response if we don't have one (in suggestion handler)
        if (!memoryUuid && prediction.memoryUuid) {
          updateMemoryUuid(prediction.memoryUuid);
          console.log('New memory UUID acquired from suggestion API:', prediction.memoryUuid);
        }
        
        responseMessage = prediction.response || prediction.content || prediction.answer || prediction.text || responseMessage;
      }
      
      setLoading(false);
      streamResponse(botMessageId, responseMessage);
    } catch (error) {
      console.log('Suggestion error details:', error);
      
      // Check if it's a memory expired error
      const isMemoryExpired = error.message?.includes('memory-expired-error') || 
                             error.message?.includes('Memory UUID not found') ||
                             (error.message?.includes('400') && memoryUuid);
      
      if (isMemoryExpired && memoryUuid) {
        console.log('Memory UUID expired on suggestion, retrying with fresh session...');
        updateMemoryUuid(null); // Clear expired UUID from state and session
        
        // Clear the request cache to ensure fresh requests
        const productId = product?.id || product?.unique_id || product?.product_id;
        clearRequestCache(productId);
        
        // Retry the request without memoryUuid to get a fresh session
        try {
          const retryRequestBody = createConversationRequest(
            product, 
            suggestionText, 
            null, // No memoryUuid for fresh session
            pdfStatus
          );

          // Convert problematic PDF links if present
          if (retryRequestBody.batch && retryRequestBody.batch[0] && retryRequestBody.batch[0].pdfLinks) {
            const originalPdfLinks = retryRequestBody.batch[0].pdfLinks;
            const convertedPdfLinks = originalPdfLinks.map(link => {
              if (link.startsWith('//s1.img-b.com/')) {
                return link.replace('//s1.img-b.com/', 'https://storage.googleapis.com/buildcom-pim-prd/');
              }
              return link;
            });
            retryRequestBody.batch[0].pdfLinks = convertedPdfLinks;
          }

          const retryData = await fetchBotResponse(retryRequestBody);
          
          let retryResponseMessage = 'The AI service responded but no content was received.';
          if (retryData.predictions && retryData.predictions[0]) {
            const prediction = retryData.predictions[0];
            
            // Extract new memoryUuid (in suggestion retry)
            if (prediction.memoryUuid) {
              updateMemoryUuid(prediction.memoryUuid);
              console.log('New memory UUID acquired after suggestion retry:', prediction.memoryUuid);
            }
            
            retryResponseMessage = prediction.response || prediction.content || prediction.answer || prediction.text || retryResponseMessage;
          }
          
          setLoading(false);
          streamResponse(botMessageId, retryResponseMessage);
          return; // Success, exit the function
        } catch (retryError) {
          console.error('Suggestion retry failed:', retryError);
        }
      }
      
      // If we get here, either it wasn't a memory error or the retry failed
      const simpleErrorMsg = 'Sorry, something went wrong. Please try again.';
      setConversations(prev => prev.map(msg => msg.id === botMessageId ? { ...msg, message: simpleErrorMsg, isStreaming: false, success: false } : msg));
      setError(null); // Don't show the error alert
      setLoading(false);
    }
  }, [loading, memoryUuid, product, pdfStatus, streamResponse, updateMemoryUuid]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      handleQuestionSubmit();
    }
  }, [handleQuestionSubmit]);

  const handleClearConversation = useCallback(() => {
    setConversations([]);
    setShowSuggestions(true);
    setQuestion('');
    setError(null);
    setLoading(false);
    // Reset memoryUuid for fresh session and clear from session storage
    updateMemoryUuid(null);
    
    // Clear request cache
    const productId = product?.id || product?.unique_id || product?.product_id;
    clearRequestCache(productId);
    
    console.log('Conversation cleared - fresh session started');
  }, [updateMemoryUuid, product]);

  const handleCopyToClipboard = useCallback(async (text, messageId) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      
      setCopiedMessageId(messageId);
      setShowCopySnackbar(true);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, []);

  const handleFeedback = useCallback((messageId, feedback) => {
    // You can implement feedback tracking here
    console.log(`Feedback for message ${messageId}: ${feedback}`);
  }, []);

  // Memoized message bubble component for better performance
  const MessageBubble = memo(({ message }) => {
    const isUser = message.type === 'user';
    const isError = message.type === 'bot' && !message.success;
    const isStreaming = message.isStreaming;
    
    // Memoized styles
    const userStyles = useMemo(() => ({
      maxWidth: '90%', 
      display: 'flex', 
      alignItems: 'flex-start', 
      gap: 1
    }), []);
    
    const botStyles = useMemo(() => ({
      maxWidth: '90%', 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1
    }), []);
    
    const paperStyles = useMemo(() => ({
      px: 1, 
      py: 0.5, 
      backgroundColor: '#f3f4f6', 
      color: '#374151', 
      borderRadius: 2, 
      maxWidth: '100%', 
      minWidth: '80px', 
      border: '1px solid #e5e7eb', 
      boxShadow: 'none'
    }), []);
    
    const typographyStyles = useMemo(() => ({
      lineHeight: 1.4, 
      whiteSpace: 'pre-wrap', 
      wordBreak: 'break-word', 
      fontSize: '0.85rem'
    }), []);
    
    return (
      <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.5, alignItems: 'flex-start', gap: 1 }}>
        {!isUser && (
          <Avatar sx={{ width: 28, height: 28, bgcolor: '#53717C', fontSize: '0.75rem' }}>
            <img src={aiAnswerIcon} alt="AI" style={{ width: '16px', height: '16px' }} />
          </Avatar>
        )}
        {isUser ? (
          <Box sx={userStyles}>
            <Paper elevation={0} sx={paperStyles}>
              <Typography variant="body2" sx={typographyStyles}>
                {message.message}
              </Typography>
            </Paper>
            <Avatar sx={{ width: 28, height: 28, bgcolor: '#B68C5C', fontSize: '0.75rem' }}>
              <img src={userIcon} alt="User" style={{ width: '16px', height: '16px' }} />
            </Avatar>
          </Box>
        ) : (
          <Box sx={{ maxWidth: '90%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={botStyles}>
              {message.message === '' && loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={12} sx={{ color: '#53717C' }} />
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    Thinking...
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ ...typographyStyles, color: '#374151' }}>
                  {isError ? 'Sorry, something went wrong. Please try again.' : message.message}
                  {isStreaming && (
                    <Box component="span" sx={{ display: 'inline-block', width: '8px', height: '16px', backgroundColor: '#374151', marginLeft: '2px', animation: 'blink 1s infinite', verticalAlign: 'text-bottom' }} />
                  )}
                </Typography>
              )}
            </Box>
            {!isError && message.message && !isStreaming && (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 0, opacity: 0.6, '&:hover': { opacity: 1 }, transition: 'opacity 0.2s' }}>
                <IconButton
                  size="small"
                  onClick={() => handleCopyToClipboard(message.message, message.id)}
                  sx={{ 
                    color: copiedMessageId === message.id ? '#22c55e' : '#6b7280',
                    fontSize: '0.7rem',
                    width: '20px',
                    height: '20px',
                    '&:hover': { backgroundColor: 'rgba(107, 114, 128, 0.1)' }
                  }}
                  title={copiedMessageId === message.id ? 'Copied!' : 'Copy response'}
                >
                  <ContentCopyIcon sx={{ fontSize: '12px' }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleFeedback(message.id, 'positive')}
                  sx={{ 
                    color: '#6b7280', 
                    fontSize: '0.7rem',
                    width: '20px',
                    height: '20px',
                    '&:hover': { backgroundColor: 'rgba(107, 114, 128, 0.1)', color: '#22c55e' }
                  }}
                  title="Good response"
                >
                  <ThumbUpIcon sx={{ fontSize: '12px' }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleFeedback(message.id, 'negative')}
                  sx={{ 
                    color: '#6b7280', 
                    fontSize: '0.7rem',
                    width: '20px',
                    height: '20px',
                    '&:hover': { backgroundColor: 'rgba(107, 114, 128, 0.1)', color: '#ef4444' }
                  }}
                  title="Poor response"
                >
                  <ThumbDownIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  });
  
  MessageBubble.displayName = 'MessageBubble';

  // Memoized container styles
  const containerStyles = useMemo(() => ({
    height: '100%',
    minHeight: '600px',
    maxHeight: '600px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid #e2e8f0'
  }), []);

  const chatContainerStyles = useMemo(() => ({
    flex: 1, 
    overflowY: 'auto', 
    p: 2, 
    display: 'flex', 
    flexDirection: 'column', 
    backgroundColor: '#fff'
  }), []);

  const headerStyles = useMemo(() => ({
    py: 0.5, 
    px: 1, 
    borderBottom: '1px solid #e2e8f0', 
    backgroundColor: '#53717C', 
    flexShrink: 0
  }), []);

  const inputBoxStyles = useMemo(() => ({
    p: 2, 
    backgroundColor: '#fff', 
    borderTop: '1px solid #e2e8f0',
    position: 'sticky',
    bottom: 0,
    zIndex: 1
  }), []);

  const placeholderText = useMemo(() => {
    if (isInitializing) {
      return 'Setting up session...';
    }
    return 'Type your question here...';
  }, [isInitializing]);

  // Safety mechanism - auto-disable initialization after 15 seconds
  useEffect(() => {
    if (isInitializing) {
      const safetyTimeout = setTimeout(() => {
        console.warn('Initialization timeout - allowing user interaction');
        setIsInitializing(false);
        setError('Session initialization took longer than expected. You can still ask questions, but some features may be limited.');
      }, 15000);
      
      return () => clearTimeout(safetyTimeout);
    }
  }, [isInitializing]);

  return (
    <>
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}
      </style>
      <Box sx={containerStyles}>
        {!hideHeader && (
          <Box sx={headerStyles}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#53717C' }}>
                <img src={headerIcon} alt="Product Q&A Agent" />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', fontSize: '1.1rem' }}>
                    Product Q&A Agent
                  </Typography>
                  {ApiStatusIndicator}
                </Box>
              </Box>
              {conversations.length > 0 && (
                <IconButton
                  onClick={handleClearConversation}
                  size="small"
                  sx={{ 
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  title="Clear conversation"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        )}
        <Box ref={chatContainerRef} sx={chatContainerStyles}>
          {conversations.length === 0 && !loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 300 }}>
                I'm here to help you learn more about this product. Ask me anything!
              </Typography>
              
              {showSuggestions && !isInitializing && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    Try asking:
                  </Typography>
                  {suggestionQuestions.map((suggestion, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        px: 1, 
                        py: 0.5, 
                        backgroundColor: '#f3f4f6', 
                        color: '#374151', 
                        borderRadius: 2, 
                        maxWidth: '400px', 
                        minWidth: '200px', 
                        border: '1px solid #e5e7eb', 
                        boxShadow: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#e5e7eb',
                          borderColor: '#d1d5db'
                        }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{
                          lineHeight: 1.4, 
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-word', 
                          fontSize: '0.85rem'
                        }}
                      >
                        {suggestion}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
          {conversations.map((message, idx) => (
            <React.Fragment key={message.id}>
              <MessageBubble message={message} />
              {message.type === 'bot' && idx < conversations.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <div ref={messagesEndRef} />
        </Box>
        <Box sx={inputBoxStyles}>
          <TextField
            fullWidth
            size="small"
            placeholder={placeholderText}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={loading || isInitializing}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={handleQuestionSubmit}
                    disabled={!question.trim() || loading || isInitializing}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '20px',
                backgroundColor: '#f8fafc'
              }
            }}
          />
        </Box>
        <Snackbar
          open={showCopySnackbar}
          autoHideDuration={2000}
          onClose={() => setShowCopySnackbar(false)}
          message="Response copied to clipboard"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </>
  );
};

export default memo(LW_Agent_Conversational);